import { useEffect, useRef, useState } from "react";
import { useDismiss, type Dismissed } from "./Dismiss";

// A row's own actions behind one "⋯" button: on /shares each share and
// recording keeps Edit, Delete and the like here rather than as buttons drawn
// on every row. A menu in the page — role="menu", its items real buttons —
// opened by the button, its first item focused; ↑/↓ move. Esc closes it and
// gives the focus back to the button; a press outside, or the focus leaving
// (Tab), closes it where the person went. A second press on the button closes
// it and gives the button the focus, in every browser (useDismiss's keepFocus).

export type MenuItem = { label: string; onSelect: () => void; danger?: boolean };

export function RowMenu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const close = (how: Dismissed) => {
    setOpen(false);
    if (how === "escape" || how === "opener") button.current?.focus();
  };
  const dismiss = useDismiss(open, close, [wrap], { onFocusOut: true });

  useEffect(() => {
    if (open) menu.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
  }, [open]);

  // nothing to offer: the menu's column stays, so the rows keep their columns
  if (items.length === 0) return <span className="row-menu" />;

  const onKey = (e: React.KeyboardEvent) => {
    dismiss.onKeyDown(e);
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const all = [...(menu.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? [])];
    const at = all.indexOf(document.activeElement as HTMLButtonElement);
    all[(at + (e.key === "ArrowDown" ? 1 : -1) + all.length) % all.length]?.focus();
  };

  return (
    <span ref={wrap} className="row-menu" onBlur={dismiss.onBlur}>
      <button
        ref={button}
        type="button"
        className="row-menu-button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseDown={dismiss.keepFocus}
        onClick={() => (open ? close("opener") : setOpen(true))}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.8" />
          <circle cx="12" cy="12" r="1.8" />
          <circle cx="19" cy="12" r="1.8" />
        </svg>
      </button>
      {open && (
        <div ref={menu} className="row-menu-list" role="menu" aria-label={label} onKeyDown={onKey}>
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              className={it.danger ? "danger" : undefined}
              onClick={() => {
                setOpen(false);
                button.current?.focus();
                it.onSelect();
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
